import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import {
  AppBar,
  Toolbar,
  TextField,
  Tooltip,
  Hidden,
  IconButton,
  FormControl
} from '@material-ui/core';
import Skeleton from 'react-loading-skeleton';
import SubdirectoryArrowRightIcon from '@material-ui/icons/SubdirectoryArrowRight';
import Autocomplete from '@material-ui/lab/Autocomplete';
import MenuIcon from '@material-ui/icons/Menu';
import InputIcon from '@material-ui/icons/Input';
import { actions as UserActions } from 'store/user';
import { actions as userMessageActions } from 'store/userMessaging';
import { dismissButton, dispatchButton } from 'helpers/error/buttonBuilders';
import { SelectInput } from 'components';
import { useLanguageSettings } from 'views/Settings';

const { setMessage } = userMessageActions;
const { setSelectedTenant, setSelectedBusinessContext, logout } = UserActions;
const useStyles = makeStyles(theme => ({
  root: {
    boxShadow: 'none',
    backgroundColor: '#404040'
  },
  spacer: {
    flexGrow: 1
  },
  margin: {
    margin: theme.spacing(1),
    minWidth: 200,
    '& .Mui-focused': {
      color: 'rgb(255, 255, 255)'
    }
  },
  toolBar: {
    paddingLeft: 0
  },
  signOutButton: {
    marginLeft: theme.spacing(1)
  },
  labelsStyle: {
    color: 'rgb(255, 255, 255)',
    '& .Mui-focused': {
      color: 'rgb(255, 255, 255)'
    }
  },
  inputRoot: {
    color: 'rgb(255, 255, 255)',
    '& .MuiInputBase-input': { color: 'rgb(255, 255, 255)' },
    '& .MuiIconButton-root': { color: 'rgb(255, 255, 255)' },
    '&[class*="MuiOutlinedInput-root"] .MuiAutocomplete-input:first-child': {
      paddingLeft: 26
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgb(255, 255, 255)'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgb(255, 255, 255)'
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgb(255, 255, 255)'
    }
  },
  hidden: {
    visibility: 'hidden'
  },
  visible: {
    color: 'grey'
  },
  item: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    width: '200px'
  },
  logo: {
    height: '30px',
    margin: '18px'
  },
  selectInput: {
    margin: theme.spacing(1),
    minWidth: 200,
    '& .Mui-focused': {
      color: 'rgb(255, 255, 255)'
    },
    '& .MuiFormLabel-root': {
      color: theme.palette.primary.contrastText
    },
    '& .color: theme.palette.primary.contrastText': {
      color: theme.palette.primary.contrastText
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: theme.palette.primary.contrastText
    },
    '& .MuiSelect-select': {
      color: theme.palette.primary.contrastText
    },
    '& .MuiSelect-icon': {
      color: theme.palette.primary.contrastText
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.primary.contrastText
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.contrastText
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.contrastText
      }
    }
  },
  selectInputOnHover: {
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: theme.palette.primary.contrastText
      }
    }
  },
  selectDisabledInputOnHover: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: `${theme.palette.primary.contrastText} !important`
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.contrastText
      }
    },
    '& .MuiSvgIcon-root': {
      visibility: 'hidden'
    }
  }
}));

const Topbar = props => {
  const { className, onSidebarOpen, ...rest } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { languageContent, getLanguageContent } = useLanguageSettings();
  const [topbarContent, setTopbarContent] = useState(null);

  const tenants = useSelector(store => store.user.tenants || []);
  const validContexts = useSelector(store => store.user.validContexts || []);
  const contextDetails = useSelector(store => store.user.contextDetails || []);
  const selectedTenant = useSelector(store => store.user.selectedTenant || {});
  const selectedContext = useSelector(
    store => store.user.selectedBusinessContext
    );
  const supportedTenantList = useSelector(store => store.user.supportedTenant || []);
  const loggedTenant = useSelector(store => store.user.loggedTenant || {});
  const isContextLoading = useSelector(store => store.user.isContextLoading || false);

  const [propertyValue, setPropertyValue] = useState(selectedContext);
  const [tenantValue, setTenantValue] = useState(selectedTenant);
  const [contextLoading, setContextLoading] = useState(isContextLoading);

  useEffect(() => {
    if (languageContent) {
      setTopbarContent(getLanguageContent('topbar'));
    }
  }, [languageContent]);

  const warnOrDispatch = (action, messageContext) => {
    const warnPaths = ['details', 'design', 'edit', 'settings', 'subscribers', 'import-status'];

    let shouldWarn = !!warnPaths.find(path =>
      history.location.pathname.includes(path)
    );

    if (shouldWarn) {
      dispatch(
        setMessage({
          title: 'Warning',
          severity: 'warning',
          instructions: `Are you sure you wish to change ${messageContext}? Any unsaved data will be lost.`,
          buttons: [dismissButton('Cancel'), dispatchButton('OK', action)]
        })
      );
    } else {
      dispatch(action);
    }
  };

  const handleTenantChange = (event, value) => {
    if (value != null || value) {
      let action = UserActions.getLicenses({ 
        isTenantSwitch: true, 
        tenantId: value.tenantId, 
        tenantName: value.name 
      });
      warnOrDispatch(action, 'the tenant');
    }
  };
  
  useEffect(() => {
    setContextLoading(isContextLoading);
  }, [isContextLoading]);

  useEffect(() => {
    setPropertyValue(selectedContext);
  }, [selectedContext]);

  useEffect(() => {
    setTenantValue(selectedTenant);
  }, [selectedTenant]);

  const handleContextChange = (event, value) => {
    if (value != null || value) {
      let action = setSelectedBusinessContext(value.businessContextId, true);
      warnOrDispatch(action, 'properties');
    }
  };

  const tenantOptions = {
    list: tenants,
    keyId: 'tenantId',
    valueId: 'tenantId',
    displayValueId: 'tenantName'
  };

  return (
    <AppBar {...rest} className={clsx(classes.root, className)}>
      <Toolbar className={classes.toolBar}>
        <RouterLink to="/">
          <img
            alt="Logo"
            className={classes.logo}
            src="images/logos/new_product_logo_full_white.svg"
          />
        </RouterLink>
        <span className={classes.spacer} />
        {((loggedTenant.tenantId === '0' && Array.isArray(loggedTenant.defaultPermissions)) || loggedTenant.tenantType === 'SERVICE_ACCOUNT') 
        ? (supportedTenantList.isGetting 
          ? (<Skeleton
            style={{ marginLeft: '8px', opacity: '0.5' }}
            variant="rectangular"
            width={200}
            height={40}
          />) 
          : (<FormControl className={clsx(classes.selectInput, classes.selectInputOnHover)}>
          <Autocomplete 
            className={classes.inputRoot}
            id="size-small-outlined"
            size="small"
            onChange={(e, value) => handleTenantChange(e, value)}
            options={supportedTenantList.list}
            value={tenantValue}
            getOptionLabel={option => option.name || option.tenantName || option.tenantId || ''}
            renderInput={params => (
              <TextField
                {...params}
                InputLabelProps={{
                  className: classes.labelsStyle
                }}
                label={topbarContent?.tenantDropdownLabel || `Select a Tenant`}
                variant="outlined"
              />
            )}
          />
        </FormControl>))
        : (<FormControl className={clsx(classes.selectInput, classes.selectDisabledInputOnHover)}>
          <SelectInput
            fullWidth
            label={topbarContent?.tenantInputLabel || `Tenant`}
            margin="dense"
            name="tenant-select"
            onChange={handleTenantChange}
            value={selectedTenant.tenantId || ''}
            options={tenantOptions}
            disabled={true}
          />
        </FormControl>)}
        <FormControl className={classes.margin}>
          {supportedTenantList.isGetting || contextLoading
          ? (<Skeleton
            style={{ marginLeft: '8px', opacity: '0.5' }}
            variant="rectangular"
            width={200}
            height={40}
          />)
          : (<Autocomplete
            className={classes.inputRoot}
            id="size-small-outlined"
            size="small"
            onChange={(e, value) => handleContextChange(e, value)}
            options={[
              { businessContextId: 'Default', name: 'ALL', level: 0 },
              ...contextDetails
            ]}
            value={propertyValue}
            getOptionDisabled={option =>
              validContexts.indexOf(option.businessContextId) < 0
            }
            getOptionLabel={option => option.name || option.contextName}
            renderOption={option => (
              <Tooltip title={option.name || option.contextName}>
                <div
                  className={classes.item}
                  onClick={() => handleContextChange(option)}>
                  {Array(option.level).fill(
                    <SubdirectoryArrowRightIcon className={classes.hidden} />
                  )}
                  {option.name !== 'ALL' && (
                    <SubdirectoryArrowRightIcon className={classes.visible} />
                  )}
                  <span className={classes.label}>
                    {option.name || option.contextName}
                  </span>
                </div>
              </Tooltip>
            )}
            renderInput={params => (
              <TextField
                {...params}
                InputLabelProps={{
                  className: classes.labelsStyle
                }}
                label={topbarContent?.propertyDropdownLabel || `Select a Property`}
                variant="outlined"
              />
            )}
          />)}
        </FormControl>
        <Hidden mdDown>
          <IconButton
            className={classes.signOutButton}
            color="inherit"
            label="Sign Out"
            title={topbarContent?.logoutTooltip || `Sign Out`}
            onClick={() => {
              dispatch(logout());
            }}>
            <InputIcon />
          </IconButton>
        </Hidden>
        <Hidden lgUp>
          <IconButton color="inherit" onClick={onSidebarOpen}>
            <MenuIcon />
          </IconButton>
        </Hidden>
      </Toolbar>
    </AppBar>
  );
};

Topbar.propTypes = {
  className: PropTypes.string,
  onSidebarOpen: PropTypes.func
};

export default Topbar;
